/**
 * Display-only member-abbreviation derivation (#1578).
 *
 * The roster's live name/abbreviation toggle (#709 / #1413-ux) renders each
 * member's STORED team abbreviation, falling back to the full name when a
 * member has none. On a team where nobody has one stored (e.g. a freshly
 * imported team), that fallback made the toggle a silent no-op — state
 * flipped, nothing visibly changed.
 *
 * This module derives a fallback initialism client-side so the toggle
 * always visibly shortens the grid. Mirrors the server generator
 * (`membership_lifecycle/abbreviations.py::generate_unique_abbreviations`):
 * initials of up to three words (first letters), single-word names take
 * their first three letters, punctuation is dropped, and collisions within
 * the rendered member set get a numeric suffix ("AB", "AB2", …) so two
 * derived values never read as the same person. Stored abbreviations are
 * reserved first and always win. Display-only — nothing is persisted, and
 * the full name stays available on the `title` attribute at every call
 * site.
 */

const MAX_INITIALS = 3;

/** Initials base for one display name — same shape the server generates. */
export function deriveAbbreviation(displayName: string): string {
  const words = displayName.split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 0);
  if (words.length === 0) return "X";
  if (words.length === 1) return (words[0] as string).slice(0, MAX_INITIALS).toUpperCase();
  return words
    .slice(0, MAX_INITIALS)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/**
 * Build `display_name → derived abbreviation` for every member WITHOUT a
 * stored abbreviation, deduped against the stored ones and against each
 * other (numeric suffixes, first claimant keeps the clean base).
 *
 * Keyed by display name because the roster surfaces resolve labels through
 * `displayNameFor({ display_name, abbreviation })` — member ids are not in
 * that signature. Duplicate display names collapse to one entry, which is
 * harmless: identical names would be indistinguishable at full length too.
 */
export function buildDerivedAbbreviations(
  members: readonly { display_name: string; abbreviation?: string | null }[],
): Map<string, string> {
  const taken = new Set<string>();
  for (const m of members) {
    if (m.abbreviation && m.abbreviation.trim() !== "") taken.add(m.abbreviation.trim());
  }
  const out = new Map<string, string>();
  for (const m of members) {
    if (m.abbreviation && m.abbreviation.trim() !== "") continue;
    if (out.has(m.display_name)) continue;
    const base = deriveAbbreviation(m.display_name);
    let candidate = base;
    let suffix = 2;
    while (taken.has(candidate)) {
      candidate = `${base}${suffix}`;
      suffix += 1;
    }
    taken.add(candidate);
    out.set(m.display_name, candidate);
  }
  return out;
}
